import { z } from 'zod'

/**
 * A projected CASL rule: exactly `[action, subject]`.
 *
 * This mirrors the backend DTO's `array<int, array{0: string, 1: string}>` on
 * purpose. CASL's pack format allows up to six positional slots
 * (`[action, subject, conditions, fields, inverted, reason]`), but the contract
 * deliberately carries none of them — instance-level answers ship as `can` flags
 * on resource DTOs, computed by the real policy.
 *
 * Being strict here is a safety property, not pedantry. A conditioned rule that
 * slipped through would make CASL answer `can('update', 'pos.order')` — a bare
 * subject-type check with no instance to evaluate against — with `true`, because
 * a condition it cannot test reads as "possibly allowed". That widens the UI
 * against a backend that never granted it. A 2-tuple has nothing to widen.
 *
 * If the backend ever does start emitting conditions, that is a contract change:
 * it bumps `casl.contract_version`, this schema changes in the same breath, and
 * until then a longer tuple fails validation and the frontend keeps the ability
 * it already had.
 */
export const packRuleSchema = z.tuple([z.string(), z.string()])

/**
 * The wire contract for `GET /admin/me/abilities` — a field-for-field mirror of
 * `Support\Casl\DTOs\UserAbilitiesData`. Field names are the backend's snake_case
 * verbatim (the DTO maps through `SnakeCaseMapper`), so there is no re-casing
 * layer that could drift.
 *
 * `permissions` is the CANONICAL field: every permission name the user holds,
 * capability flags (`nova`, `login`) included. `rules` is derived — the same set
 * projected into CASL pairs by `Support\Casl\CaslProjection`, with capability
 * flags absent because they have no action segment to split.
 *
 * Every field is required. This schema is the single validation choke point, and
 * a payload missing `permissions` or `tenant_id` is a backend this frontend does
 * not understand — better to fail closed and keep the prior ability than to read
 * "absent" as "holds nothing" (hiding UI the user is entitled to) or, for
 * `tenant_id`, to lose the ability to notice a tenant switch at all.
 *
 * `.passthrough()` keeps unknown keys, so the backend can add a field without
 * frontend coordination.
 */
export const abilitiesResponseSchema = z
  .object({
    version: z.string(),
    tenant_id: z.string(),
    subject: z.object({
      id: z.string(),
      type: z.literal('user')
    }),
    permissions: z.array(z.string()),
    rules: z.array(packRuleSchema)
  })
  .passthrough()
